import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import {
    ContactSupportDialogSectionType,
    ContactSupportOptionType,
} from '@fi-shared/fi-support/fi-contact-support-dialog/fi-contact-support-dialog.component';
import { IFiCustomerSupportContact } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiContactSupportSectionBase } from '@fi-shared/fi-support/fi-contact-support-dialog/fi-contact-support-section-base';
import { fiHasAccessToApplicationType } from '@fi-shared/utils';
import { AccessRightType, ApplicationSettingsType } from '@fi-shared/types';
import { ISectionCardData } from '@fi-shared/fi-section-card/fi-section-card';

@Component({
    selector: 'fi-contact-support-main-section',
    template: `
        <div class="section-header">
          <fi-contact-support-section-header
            [sectionHeaderText]="'feedback.contact_support' | fiAppText"
            [sectionDescriptionText]="'support.main_section_description' | fiAppText"
            (onCloseDialog)="closeDialog()"
            >
          </fi-contact-support-section-header>
        </div>
        
        <div class="section-content-container">
          @for (availableContactOption of availableContactOptions; track availableContactOption) {
            <fi-section-card
              class="cursor-pointer"
              [contentCardData]="availableContactOption"
              >
            </fi-section-card>
          }
        </div>
        `,
    styleUrls: ['./fi-contact-support-main-section.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-contact-support-main-section' },
    standalone: false,
})
export class FiContactSupportMainSectionComponent extends FiContactSupportSectionBase implements OnInit {
    availableContactOptions: ISectionCardData[] = [];

    ngOnInit(): void {
        if (!this.currentUserSupportManager) {
            this.availableContactOptions.push({
                headerTextLabel: this.appTextService.get('support.option_account_header'),
                subheaderTextLabel: this.appTextService.get('support.option_account_description'),
                id: ContactSupportOptionType.Account,
                onClick: () => {
                    this.setCurrentSupportOptionType(ContactSupportOptionType.Account);
                    this.setCurrentSupportSectionType(
                        this.isChatOptionEnabled()
                            ? ContactSupportDialogSectionType.ContactMethod
                            : ContactSupportDialogSectionType.Email,
                    );
                },
            });
            this.availableContactOptions.push({
                headerTextLabel: this.appTextService.get('support.option_content_header'),
                subheaderTextLabel: this.appTextService.get('support.option_content_description'),
                id: ContactSupportOptionType.Content,
                onClick: () => {
                    this.setCurrentSupportOptionType(ContactSupportOptionType.Content);
                    this.setCurrentSupportSectionType(
                        this.isChatOptionEnabled()
                            ? ContactSupportDialogSectionType.ContactMethod
                            : ContactSupportDialogSectionType.Email,
                    );
                },
            });
        }

        this.availableContactOptions.push({
            headerTextLabel: this.appTextService.get('support.option_technical_header'),
            subheaderTextLabel: this.appTextService.get('support.option_technical_description'),
            id: ContactSupportOptionType.Technical,
            onClick: () => {
                this.setCurrentSupportOptionType(ContactSupportOptionType.Technical);
                this.setCurrentSupportSectionType(ContactSupportDialogSectionType.Email);
            },
        });

        if (!this.currentUserSupportManager) {
            this.availableContactOptions.push({
                headerTextLabel: this.appTextService.get('support.option_feedback_header'),
                subheaderTextLabel: this.appTextService.get('support.option_feedback_description'),
                id: ContactSupportOptionType.Feedback,
                onClick: () => {
                    this.setCurrentSupportOptionType(ContactSupportOptionType.Feedback);
                    this.setCurrentSupportSectionType(ContactSupportDialogSectionType.Feedback);
                },
            });
        } else {
            this.availableContactOptions.push({
                headerTextLabel: this.appTextService.get('support.option_feedback_chainformation_header'),
                subheaderTextLabel: this.appTextService.get('support.option_feedback_chainformation_description'),
                id: ContactSupportOptionType.FeedbackChainformation,
                onClick: () => {
                    this.setCurrentSupportOptionType(ContactSupportOptionType.FeedbackChainformation);
                    this.setCurrentSupportSectionType(ContactSupportDialogSectionType.Email);
                },
            });
        }
    }

    private isChatOptionEnabled(): boolean {
        const hasAccessToChat =
            this.userDataService.userData &&
            fiHasAccessToApplicationType(
                ApplicationSettingsType.Chat,
                AccessRightType.Read,
                this.userDataService.userData.ModuleAccessRights,
            );
        if (!hasAccessToChat) return false;

        const supportContact: IFiCustomerSupportContact = this.customerDataService.getCustomerData().SupportContact;

        if (
            !supportContact.AllowSupportChat ||
            !supportContact.SupportManagerId ||
            this.userDataService.getUserId() === supportContact.SupportManagerId
        )
            return false;

        return this.fiSignalHelperRService.isUserOnline(supportContact.SupportManagerId);
    }
}
